import * as React from 'react'

import { Box, Button, Text, useClassNames } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  ProfileEmployeeLocationFragment,
  ProfileEmployeeTeamFragment,
  ProfileFeatureAvailabilityFragment,
  ProfileFeatureFlagFragment,
  ProfileModuleAvailabilityFragment,
  ResourceFieldDataFragment,
  ValueDataFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Form from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import { Panes } from 'modules/core/components/Panes'
import PanesHeader from 'modules/core/components/PanesHeader'
import PanesPadForm from 'modules/core/components/PanesPadForm'
import {
  Employee as CurrentEmployee,
  useCurrentSession,
} from 'modules/core/hooks/useCurrentSession'
import { isValidEmail } from 'modules/core/lib/email'
import i18n from 'modules/core/lib/i18n'

import CustomFieldsForm from 'modules/custom_resources/components/CustomFields/CustomFieldsForm'
import { filterCustomResourceFieldsDataByUsageGroup } from 'modules/custom_resources/lib/custom-resource-utils'
import EmployeeCostCentersAssignmentsWithHistory from 'modules/employees/components/CostCenters/index'
import FaceRecognition from 'modules/employees/components/blocks/FaceRecognition'
import type WorkForm from 'modules/employees/lib/forms/EmployeeForm/WorkInformation'
import type UserForm from 'modules/employees/lib/forms/Personal/User'
import WithPaywall from 'modules/finance/components/WithPaywall'
import EmployeeCostCenters from 'modules/finance/public/EmployeeCostCenters'
import EmployeeCostCentersWithHistory from 'modules/finance/public/EmployeeCostCentersWithHistory'
import EmployeeDimensions from 'modules/finance/public/EmployeeDimensions'
import { getEmployeeJobRole } from 'modules/job_catalog/lib/utils'
import EmployeeItAssets from 'modules/profile/components/EmployeeItAssets'
import PermissionGroupInformation from 'modules/profile/components/PermissionGroupInformation'
import { ProfileEmployeeData } from 'modules/profile/components/ProfileForm/types'
import Teams from 'modules/profile/components/Teams'
import WorkInformationForm from 'modules/profile/components/WorkInformationForm'
import Workplaces from 'modules/profile/components/Workplaces'
import type EmailForm from 'modules/profile/lib/forms/Email'
import type PersonalEmailForm from 'modules/profile/lib/forms/PersonalEmail'
import { useModule as useTimeoffModule } from 'modules/timeoff/hooks/useModule'
import { hasPolicyTemporality } from 'modules/timeoff/lib/features/TimeoffFeatures'

import AvatarForm from './AvatarForm'
import TimeoffForm from './Timeoff'
import * as styles from './index.vanilla.css'

type FormProps = React.ComponentProps<typeof Form>

interface Props {
  onSubmitWorkForm: FormProps['onSubmit']
  onDeleteAvatar: () => void
  currentEmployee: CurrentEmployee
  employeeData: ProfileEmployeeData
  workForm: WorkForm
  emailForm: EmailForm
  personalEmailForm: PersonalEmailForm
  userForm: UserForm
  seeOldCostCentersAssignments: boolean
  permissionGroupName: string
  permissionGroupId: string
  customResourceFieldsData: ResourceFieldDataFragment[]
  customValuesData: ValueDataFragment[]
  companyTeams: ProfileEmployeeTeamFragment[]
  moduleAvailabilities: ProfileModuleAvailabilityFragment[]
  featureAvailabilities: ProfileFeatureAvailabilityFragment[]
  features: ProfileFeatureFlagFragment[]
  companyLocations: ProfileEmployeeLocationFragment[]
  canSeeTeams: boolean
  canManageTeams: boolean
  canSeePersonalInfo: boolean
  canSeePermissions: boolean
  canSeeCustomDimensions: boolean
}

const Profile = ({
  onSubmitWorkForm,
  onDeleteAvatar,
  currentEmployee,
  employeeData,
  workForm,
  emailForm,
  personalEmailForm,
  userForm,
  seeOldCostCentersAssignments,
  permissionGroupName,
  permissionGroupId,
  customResourceFieldsData,
  customValuesData,
  companyTeams,
  moduleAvailabilities,
  featureAvailabilities,
  features,
  companyLocations,
  canSeeTeams,
  canManageTeams,
  canSeePersonalInfo,
  canSeePermissions,
  canSeeCustomDimensions,
}: Props) => {
  const cx = useClassNames(styles)
  const { company } = useCurrentSession()

  const isCurrentEmployee = currentEmployee?.id === employeeData?.id
  const fullname = employeeData?.fullName ?? ''

  const canReadLeaves = employeeData?._policies?.readLeaves?.can ?? false

  const hasFeatureAvailability = (id: string) => {
    return featureAvailabilities.find((feature) => feature.id === id)?.available ?? false
  }

  const hasModuleAvailability = (id: string) => {
    return moduleAvailabilities.find((module) => module.id === id)?.available ?? false
  }

  const canEditAvatar = (): boolean => {
    return employeeData?._policies?.editPersonalInfo?.can ?? false
  }

  const renderDescription = () => {
    if (isCurrentEmployee) {
      return i18n.t(`time_off.employee_profile.description.current`)
    }
    return hasPolicyTemporality()
      ? i18n.t(`time_off.employee_profile.description.admin_policy_timeline`)
      : i18n.t(`time_off.employee_profile.description.admin`)
  }

  const hasFeature = (name: string) => {
    return features.find((feature) => feature.name === name)?.enabled ?? false
  }
  const hasPackagingPaywallsFeature = hasFeature('dev_packaging_paywalls')
  const hasDevCostCentersV2Feature = hasFeature('dev_cost_centers_v2')
  const hasFaceRecognitionFeature = hasFeature('face_recognition')
  const hasTTFaceRecognitionFeatureAvailability = hasFeatureAvailability(
    'time_tracking_face_recognition'
  )

  const itManagementEnabled = hasModuleAvailability('it_management_v2')

  const faceRecognitionEnabled = hasPackagingPaywallsFeature
    ? hasTTFaceRecognitionFeatureAvailability
    : hasFaceRecognitionFeature

  const padFormDirection = 'horizontal'
  const boxed = true

  const email = emailForm.get('email').value
  const personalEmail: string | null | undefined = personalEmailForm.get('personal_email').value
  const employeeLocations =
    employeeData?.locations?.map((location) => location?.location).filter(Boolean) ?? []

  const { loading: loadingTimeoff, enabled: timeoffEnabled } = useTimeoffModule()

  const canAssignLocations = employeeData?._policies?.bulkAssignLocations?.can ?? false
  const EmployeeCostCentersWithHistoryComponent = hasDevCostCentersV2Feature
    ? EmployeeCostCentersAssignmentsWithHistory
    : EmployeeCostCentersWithHistory

  const teamIdsSignature = employeeData.memberships
    .map((m) => m?.team?.id)
    .filter(Boolean)
    .join(',')

  return (
    <Panes direction="vertical">
      <PanesPadForm direction={padFormDirection}>
        <>
          <PanesHeader
            illustration="building"
            title={i18n.t('employee_form.work_information')}
            description={i18n.tCurrent('employee_form.work_information_info', isCurrentEmployee)}
          />
        </>
        <Form
          onSubmit={onSubmitWorkForm}
          className={cx('form.withAvatar', { 'form.boxed': boxed })}
        >
          <Box className={styles.avatar}>
            <AvatarForm
              readonly={!canEditAvatar()}
              userForm={userForm}
              avatar={employeeData?.access?.avatar ?? ''}
              avatarFull={employeeData?.access?.avatarFull ?? ''}
              fullName={employeeData?.fullName ?? ''}
              onDeleteAvatar={onDeleteAvatar}
            />
          </Box>
          <>
            <Box textAlign="center">
              <Text.Header2>{fullname}</Text.Header2>
            </Box>
            <Box paddingBottom={employeeData?.pronouns ? 's2' : 's16'} textAlign="center">
              <Text.Normal size="300" weight="medium">
                {getEmployeeJobRole(employeeData?.referenceContract)}
              </Text.Normal>
            </Box>
            {employeeData?.pronouns && (
              <Box paddingBottom="s16" textAlign="center">
                <Text.Normal size="200">{employeeData?.pronouns}</Text.Normal>
              </Box>
            )}
          </>

          <WorkInformationForm
            emailForm={emailForm}
            personalEmailForm={personalEmailForm}
            workForm={workForm}
            employeeData={employeeData}
            canSeePersonalInfo={canSeePersonalInfo}
          />

          {(emailForm.isDirty ||
            personalEmailForm.isDirty ||
            workForm.isDirty ||
            userForm.isDirty) && (
            <FormRow footer>
              <Button
                submit
                disabled={
                  (!isValidEmail(email) && emailForm.isDirty) ||
                  (!isValidEmail(personalEmail, { allowEmpty: true }) && personalEmailForm.isDirty)
                }
              >
                {i18n.t('common.submit')}
              </Button>
            </FormRow>
          )}
        </Form>
      </PanesPadForm>

      <Workplaces
        employeeId={employeeData.id}
        employeeLocations={employeeLocations}
        companyLocations={companyLocations}
        accessId={employeeData.access?.id}
        initialDefaultLocationId={employeeData.locationId ?? null}
        canAssignLocations={canAssignLocations}
      />

      {!loadingTimeoff && timeoffEnabled && canReadLeaves && (
        <PanesPadForm direction={padFormDirection} boxed={boxed}>
          <PanesHeader
            illustration="calendar-clock"
            title={i18n.t('time_off.employee_profile.title')}
            description={renderDescription()}
          />
          {employeeData?.access?.id && (
            <TimeoffForm
              employeeId={employeeData.id}
              accessId={employeeData.access.id}
              employeeFullName={employeeData.fullName ?? ''}
              timeoffManager={employeeData.timeoffManager ?? null}
              timeoffPolicyId={employeeData.timeoffPolicy?.id ?? null}
              tenureStartDate={null}
              companyId={company.id}
              canEditManager={employeeData._policies?.editManager?.can ?? false}
            />
          )}
        </PanesPadForm>
      )}
      {canSeeTeams && (
        <Teams
          employeeData={employeeData}
          isCurrentEmployee={isCurrentEmployee}
          teams={companyTeams}
          canManageTeams={canManageTeams}
        />
      )}
      {itManagementEnabled && <EmployeeItAssets employeeId={employeeData.id} />}
      {canSeePermissions && (
        <PermissionGroupInformation id={permissionGroupId} name={permissionGroupName} />
      )}
      <WithPaywall features={[Features.finance_cost_center_management]} fallback={<></>}>
        {() => (
          <>
            {canSeeCustomDimensions && employeeData?.legalEntity?.id ? (
              <EmployeeDimensions
                employeeId={employeeData.id}
                employeeFullName={employeeData.fullName ?? ''}
                legalEntityId={employeeData.legalEntity.id}
                isCurrentEmployee={isCurrentEmployee}
                managerId={employeeData.manager?.id ?? null}
                locationId={employeeData.locationId ?? null}
                teamIdsSignature={teamIdsSignature}
              />
            ) : (
              <></>
            )}
            {seeOldCostCentersAssignments ? (
              employeeData.legalEntity?.id ? (
                <EmployeeCostCenters
                  employeeId={employeeData.id}
                  employeeFullName={employeeData.fullName ?? ''}
                  legalEntityId={employeeData.legalEntity.id}
                  managerId={employeeData.manager?.id ?? null}
                  locationId={employeeData.locationId ?? null}
                  teamIdsSignature={teamIdsSignature}
                />
              ) : (
                <></>
              )
            ) : (
              <EmployeeCostCentersWithHistoryComponent
                employeeId={employeeData.id}
                employeeFullName={employeeData.fullName ?? ''}
                managerId={employeeData.manager?.id ?? null}
                locationId={employeeData.locationId ?? null}
                teamIdsSignature={teamIdsSignature}
              />
            )}
          </>
        )}
      </WithPaywall>

      <CustomFieldsForm
        boxed={boxed}
        customResourceFieldsData={filterCustomResourceFieldsDataByUsageGroup(
          customResourceFieldsData,
          'employee-profile'
        )}
        customValuesData={customValuesData}
        description={i18n.t('employee_form.custom_fields.basic_information.description')}
        icon="input-field"
        padFormDirection={padFormDirection}
        title={i18n.t('employee_form.custom_fields.basic_information.title')}
        valuableId={employeeData.id}
        valuableType="Employee"
        currency={employeeData.legalEntity?.currency}
      />

      <CustomFieldsForm
        boxed={boxed}
        customResourceFieldsData={filterCustomResourceFieldsDataByUsageGroup(
          customResourceFieldsData,
          'employees-questions'
        )}
        customValuesData={customValuesData}
        padFormDirection={padFormDirection}
        valuableId={employeeData.id}
        valuableType="Employee"
        currency={employeeData.legalEntity?.currency}
      />

      {faceRecognitionEnabled && (
        <FaceRecognition
          employeeId={employeeData.id}
          employeeIdentifier={employeeData?.companyIdentifier ?? employeeData?.identifier ?? null}
        />
      )}
    </Panes>
  )
}

export default observer(Profile)
